import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Button, Card, CardBody, Input, Row, Col, Form, FormGroup, Label } from "reactstrap";
import TableContainer from "../../components/Common/TableContainer";
import { Link } from "react-router-dom";

const LatestTransaction = ({ selectedPhase }) => {
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [modal1, setModal1] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fetchedRecordsCount, setFetchedRecordsCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const recordsPerPage = 30;

  const apiUrl = process.env.REACT_APP_API_URL;

  // Calculate total pages based on total records and records per page
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const toggleViewModal = () => setModal1(!modal1);

  const handleTokenExpiration = () => {
    localStorage.removeItem("authUser");  // Clear stored user data
    navigate("/login");                   // Redirect to login page
  };

  // Fetch cards with pagination, search filter, and selected phase
  useEffect(() => {
    const fetchCards = async () => {
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser"));
        const token = authUser?.token;
        const role = authUser?.role;
        if (!token) throw new Error("Authentication token not found");
        const userId = localStorage.getItem("userId")

        const queryParams = new URLSearchParams({
          page,
          limit: recordsPerPage,
          search,
          ...(selectedPhase && { phase: selectedPhase }), // Add phase if selected
          userId
        });

        const response = await fetch(`${apiUrl}/api/data/table?${queryParams}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-User-Role": role,
          },
        });

        if (response.status === 401) {
          handleTokenExpiration();
          return;
        }

        if (!response.ok) throw new Error("Error fetching records");

        const data = await response.json();
        
        // Assuming the API now returns an object with 'records' array and 'totalCount' property
        // If your API has a different structure, adjust this accordingly
        if (data.records && typeof data.totalCount === 'number') {
          setCards(data.records);
          setFetchedRecordsCount(data.records.length);
          setTotalRecords(data.totalCount);
        } else {
          // Fallback for existing API structure
          setCards(data);
          setFetchedRecordsCount(data.length);
          // Note: Without API changes, we won't have an accurate total count
        }
      } catch (error) {
        console.error("Error fetching cards:", error);
      }
    };

    fetchCards();
  }, [page, search, selectedPhase]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const normalizeFieldNames = (field) => {
    return field
      .replace(/duration|_date|_status|_notes|_number|_text/gi, "")
      .replace(/_+/g, " ")
      .trim()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const viewCardDetails = async (cardId) => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      const role = authUser?.role;
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(`${apiUrl}/api/data/table/${cardId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": role,
        },
      });

      if (!response.ok) throw new Error("Error fetching card details");
      const data = await response.json();

      const filteredData = Object.keys(data).reduce((acc, key) => {
        if (data[key] !== null) {
          acc[normalizeFieldNames(key)] = data[key];
        }
        return acc;
      }, {});

      setSelectedCard(filteredData);
      // Set notes if they exist in the data
      setNotes(
        data["Portal"] ??
        data.portal ??
        data.internal_notes ??
        data.portal_notes ??
        data.notes ??
        ""
      );
      setSaveSuccess(false);
      toggleViewModal();
    } catch (error) {
      console.error("Error fetching card details:", error);
    }
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
    // Reset success message when user starts typing
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const saveNotes = async () => {
    if (!selectedCard) return;
    
    // Use the correct property name "Card Id" with a space
    const cardId = selectedCard["Card Id"];
    
    console.log("Selected card ID:", cardId);
    
    if (!cardId) {
      console.error("Card ID is undefined. Selected card:", selectedCard);
      return;
    }
    
    try {
      setIsSaving(true);
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      const role = authUser?.role;
      if (!token) throw new Error("Authentication token not found");
  
      console.log(`Saving notes for card ${cardId}`);
      
      const response = await fetch(`${apiUrl}/api/data/table/${cardId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          "X-User-Role": role,
        },
        body: JSON.stringify({ notes }),
      });
  
      if (response.status === 401) {
        handleTokenExpiration();
        return;
      }
  
      if (!response.ok) throw new Error("Error saving notes");
      
      setSaveSuccess(true);
      
      // Update the card in the list with new notes
      setCards(prevCards => 
        prevCards.map(card => 
          card.card_id === cardId
            ? { ...card, notes } 
            : card
        )
      );
    } catch (error) {
      console.error("Error saving notes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "card_id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => (
          <Link to="#" className="text-body fw-bold">
            {cellProps.row.original.card_id}
          </Link>
        ),
      },
      {
        header: "Name",
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Current Phase",
        accessorKey: "current_phase",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Seller",
        accessorKey: "seller_email",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          return new Date(cellProps.row.original.created_at).toLocaleDateString();
        },
      },
      {
        header: "View Details",
        enableColumnFilter: false,
        enableSorting: false,
        accessorKey: "view",
        cell: (cellProps) => (
          <Button
            type="button"
            color="primary"
            className="btn-sm btn-rounded"
            onClick={() => viewCardDetails(cellProps.row.original.card_id)}
          >
            View Details
          </Button>
        ),
      },
    ],
    []
  );

  const handleNextPage = () => setPage(page + 1);
  const handlePreviousPage = () => setPage(page > 1 ? page - 1 : 1);

  return (
    <React.Fragment>
      <Row className="mb-3">
        <Col md="6">
          <Input
            type="text"
            placeholder="Search by card ID or name..."
            value={search}
            onChange={handleSearchChange}
          />
        </Col>
      </Row>

      {modal1 && selectedCard && (
        <div className="modal show" style={{ display: "block" }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Card Details</h5>
                <Button type="button" className="btn-close" onClick={toggleViewModal} />
              </div>
              <div className="modal-body">
                {Object.keys(selectedCard).map((key) => {
                  let value = selectedCard[key];

                  // Check if the value is a date-time string and format it
                  if (typeof value === 'string' && value.includes('T') && !isNaN(Date.parse(value))) {
                    const date = new Date(value);
                    value = date.toISOString().split('T')[0]; // Format to "YYYY-MM-DD"
                  }

                  return (
                    <p key={key}>
                      <strong>{key}:</strong> {value}
                    </p>
                  );
                })}
                
                {/* Notes Section */}
                <Form className="mt-4">
                  <FormGroup>
                    <Label for="cardNotes" className="fw-bold">Notes</Label>
                    <Input
                      type="textarea"
                      id="cardNotes"
                      rows="6"
                      value={notes}
                      onChange={handleNotesChange}
                      placeholder="Add your notes here..."
                      style={{ minHeight: "150px" }}
                    />
                  </FormGroup>
                  <div className="d-flex align-items-center">
                    <Button 
                      color="success" 
                      onClick={saveNotes} 
                      disabled={isSaving}
                      className="mt-2"
                    >
                      {isSaving ? "Saving..." : "Save Notes"}
                    </Button>
                    {saveSuccess && (
                      <span className="text-success ms-3">
                        <i className="mdi mdi-check-circle me-1"></i>
                        Notes saved successfully!
                      </span>
                    )}
                  </div>
                </Form>
              </div>
              <div className="modal-footer">
                <Button color="secondary" onClick={toggleViewModal}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardBody>
          <div className="mb-4 h4 card-title">Latest Cards</div>
          <TableContainer
            columns={columns}
            data={cards}
            isGlobalFilter={false}
            tableClass="align-middle table-nowrap mb-0"
            theadClass="table-light"
          />

          <div className="pagination-controls mt-3 d-flex align-items-center justify-content-between">
            <div>
              <Button color="primary" onClick={handlePreviousPage} disabled={page === 1}>
                Previous
              </Button>
              <span className="mx-2">
                Page {page} of {totalPages > 0 ? totalPages : 1}
              </span>
              <Button 
                color="primary" 
                onClick={handleNextPage} 
                disabled={fetchedRecordsCount < recordsPerPage || page >= totalPages}
              >
                Next
              </Button>
            </div>
            <div>
              <span className="text-muted">
                Total Records: {totalRecords}
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

LatestTransaction.propTypes = {
  selectedPhase: PropTypes.string,
};

export default withRouter(LatestTransaction);